import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMenuChangeStore } from '../../../stores/appMenuChangeStore';
import { secondaryText } from '../../../styling/stylingHelper';
import { cn } from '../../../lib/utils';

/** Shared by timeline rows and dashboard task cards. Eligibility is resolved by the server. */
export default function TaskReminder({ rolloutId, taskId }: { rolloutId: string; taskId: string }) {
  const { t, i18n } = useTranslation();
  const reminder = useMenuChangeStore(s => s.remindersByRollout[rolloutId]?.[taskId]);
  const failedToLoad = useMenuChangeStore(s => s.reminderErrors.has(rolloutId));
  const sending = useMenuChangeStore(s => s.sendingReminders.has(`${rolloutId}:${taskId}`));
  const loadReminders = useMenuChangeStore(s => s.loadReminders);
  const sendReminder = useMenuChangeStore(s => s.sendReminder);
  const [feedback, setFeedback] = useState<'sent' | 'error' | null>(null);

  useEffect(() => {
    if (!reminder?.nextAllowedAt) return;
    const remaining = new Date(reminder.nextAllowedAt).getTime() - Date.now();
    if (remaining <= 0) {
      if (reminder.reason === 'cooldown') void loadReminders(rolloutId, true);
      return;
    }
    const timer = window.setTimeout(() => { void loadReminders(rolloutId, true); }, remaining + 100);
    return () => window.clearTimeout(timer);
  }, [reminder?.nextAllowedAt, reminder?.reason, rolloutId, loadReminders]);

  const formatTime = (value: string) => new Date(value).toLocaleString(i18n.language === 'en' ? 'en-GB' : 'nl-NL', { timeZone: 'Europe/Amsterdam', dateStyle: 'medium', timeStyle: 'short' });
  if (!reminder) return failedToLoad ? <div className={cn(secondaryText, 'mt-2 text-xs')}>
    <span>{t('menuChange.reminders.loadFailed')} </span>
    <button type="button" onClick={event => { event.stopPropagation(); void loadReminders(rolloutId, true); }} className="underline">{t('common.retry')}</button>
  </div> : null;
  if (reminder.daysOverdue < 1 || reminder.reason === 'done' || reminder.reason === 'cancelled') return null;

  return <div className="mt-2 space-y-1 text-xs" onClick={event => event.stopPropagation()}>
    {reminder.recipient && <p className={cn(secondaryText, 'break-words')}>{t('menuChange.reminders.recipient', { name: reminder.recipient.name, email: reminder.recipient.email })}</p>}
    <p className={secondaryText}>{reminder.lastSentAt ? t('menuChange.reminders.lastSent', { date: formatTime(reminder.lastSentAt) }) : t('menuChange.reminders.neverSent')}</p>
    <button type="button" disabled={sending || !reminder.eligible}
      onClick={async () => {
        setFeedback(null);
        try { await sendReminder(rolloutId, taskId); setFeedback('sent'); }
        catch { setFeedback('error'); }
      }}
      className="min-h-[44px] px-2 py-1 rounded-lg border border-borderPrimary dark:border-borderPrimary-dark text-textGreen dark:text-textGreen-dark disabled:opacity-50 disabled:cursor-not-allowed">
      {t(sending ? 'menuChange.reminders.sending' : 'menuChange.reminders.send')}
    </button>
    {reminder.reason === 'unassigned' && <p className={secondaryText}>{t('menuChange.reminders.unassigned')}</p>}
    {reminder.reason === 'recipient_unavailable' && <p className={secondaryText}>{t('menuChange.reminders.recipientUnavailable')}</p>}
    {reminder.reason === 'cooldown' && reminder.nextAllowedAt && <p className={secondaryText}>{t('menuChange.reminders.cooldown', { date: formatTime(reminder.nextAllowedAt) })}</p>}
    {feedback && <p role={feedback === 'error' ? 'alert' : 'status'} className={feedback === 'error' ? 'text-textRed dark:text-textRed-dark' : 'text-textGreen dark:text-textGreen-dark'}>{t(`menuChange.reminders.${feedback}`)}</p>}
  </div>;
}
